import { getLocalAddressLibrary, getLocalAddressLibraryMeta } from "@/templates/_core/addressLibraries";

const PHONE_PRESETS = {
  tw: {
    maxLength: 10,
    pattern: /^09\d{8}$/,
    payment: {
      labelKey: "payment.phoneWithTwHint",
      hintKey: "",
      placeholderKey: "payment.enterPhone",
      errorKey: "payment.invalidTwPhone",
    },
    addressModal: {
      labelKey: "addAddressModal.phone",
      hintKey: "",
      placeholderKey: "addAddressModal.enterPhone",
      errorKey: "addAddressModal.invalidTwPhone",
    },
  },
  au: {
    maxLength: 11,
    pattern: /^614\d{8}$/,
    payment: {
      labelKey: "payment.phone",
      hintKey: "payment.phoneAustraliaExampleHint",
      placeholderKey: "payment.enterPhone",
      errorKey: "payment.invalidAuPhone",
    },
    addressModal: {
      labelKey: "addAddressModal.phone",
      hintKey: "addAddressModal.phoneAustraliaExampleHint",
      placeholderKey: "addAddressModal.enterPhone",
      errorKey: "addAddressModal.invalidAuPhone",
    },
  },
};

const DELIVERY_MODE_OPTIONS = ["delivery", "pickup_711"];

const DEFAULT_CHECKOUT_FORM_CONFIG = {
  shippingAddress: {
    fields: {
      postalCode: {
        enabled: false,
        required: false,
        normalize: "text",
        maxLength: 16,
        pattern: "",
        requiredMessageKey: "payment.enterPostalCode",
        invalidMessageKey: "payment.enterPostalCode",
        payment: {
          labelKey: "payment.postalCode",
          placeholderKey: "payment.enterPostalCode",
        },
        addressModal: {
          labelKey: "payment.postalCode",
          placeholderKey: "payment.enterPostalCode",
        },
      },
    },
  },
};

const DEFAULT_CHECKOUT_CONFIG = {
  lineId: {
    enabled: true,
    required: true,
  },
  phone: {
    preset: "tw",
  },
  delivery: {
    enabledModes: ["delivery", "pickup_711"],
  },
  payment: {
    enabledMethods: ["cod"],
    defaultMethod: "cod",
    provider: null,
    currency: "TWD",
  },
  continuePayment: {
    mode: "legacy",
    provider: null,
    cashierPath: "/sub/payment/cashier",
    paymentPath: "/payment",
  },
  addressLibrary: {
    source: "remote",
    dataset: "",
  },
  forms: DEFAULT_CHECKOUT_FORM_CONFIG,
};

export function resolveTemplateCheckoutConfig(templateCheckoutConfig = {}) {
  const rawEnabledDeliveryModes = Array.isArray(templateCheckoutConfig.delivery?.enabledModes)
    ? templateCheckoutConfig.delivery.enabledModes
    : DEFAULT_CHECKOUT_CONFIG.delivery.enabledModes;
  const normalizedEnabledDeliveryModes = rawEnabledDeliveryModes
    .map((mode) => String(mode || "").trim().toLowerCase())
    .filter((mode, index, list) => DELIVERY_MODE_OPTIONS.includes(mode) && list.indexOf(mode) === index);
  const enabledDeliveryModes = normalizedEnabledDeliveryModes.length
    ? normalizedEnabledDeliveryModes
    : [...DEFAULT_CHECKOUT_CONFIG.delivery.enabledModes];
  const rawEnabledMethods = Array.isArray(templateCheckoutConfig.payment?.enabledMethods)
    ? templateCheckoutConfig.payment.enabledMethods
    : DEFAULT_CHECKOUT_CONFIG.payment.enabledMethods;
  const normalizedEnabledMethods = rawEnabledMethods
    .map((method) => String(method || "").trim().toLowerCase())
    .filter((method, index, list) => ["cod", "online"].includes(method) && list.indexOf(method) === index);
  const enabledMethods = normalizedEnabledMethods.length
    ? normalizedEnabledMethods
    : [...DEFAULT_CHECKOUT_CONFIG.payment.enabledMethods];
  const configuredDefaultMethod = String(
    templateCheckoutConfig.payment?.defaultMethod
      || DEFAULT_CHECKOUT_CONFIG.payment.defaultMethod
  ).trim().toLowerCase();
  const defaultMethod = enabledMethods.includes(configuredDefaultMethod)
    ? configuredDefaultMethod
    : enabledMethods[0];
  const mergedConfig = {
    ...DEFAULT_CHECKOUT_CONFIG,
    ...templateCheckoutConfig,
    lineId: {
      ...DEFAULT_CHECKOUT_CONFIG.lineId,
      ...(templateCheckoutConfig.lineId || {}),
    },
    phone: {
      ...DEFAULT_CHECKOUT_CONFIG.phone,
      ...(templateCheckoutConfig.phone || {}),
    },
    delivery: {
      ...DEFAULT_CHECKOUT_CONFIG.delivery,
      ...(templateCheckoutConfig.delivery || {}),
      enabledModes: enabledDeliveryModes,
    },
    payment: {
      ...DEFAULT_CHECKOUT_CONFIG.payment,
      ...(templateCheckoutConfig.payment || {}),
      enabledMethods,
      defaultMethod,
      currency: String(
        templateCheckoutConfig.payment?.currency
          || DEFAULT_CHECKOUT_CONFIG.payment.currency
      ).trim().toUpperCase(),
    },
    continuePayment: {
      ...DEFAULT_CHECKOUT_CONFIG.continuePayment,
      ...(templateCheckoutConfig.continuePayment || {}),
      mode: String(
        templateCheckoutConfig.continuePayment?.mode
          || DEFAULT_CHECKOUT_CONFIG.continuePayment.mode
      ).trim().toLowerCase(),
      provider: String(
        templateCheckoutConfig.continuePayment?.provider
          || templateCheckoutConfig.payment?.provider
          || DEFAULT_CHECKOUT_CONFIG.continuePayment.provider
          || ""
      ).trim().toLowerCase() || null,
      cashierPath: String(
        templateCheckoutConfig.continuePayment?.cashierPath
          || DEFAULT_CHECKOUT_CONFIG.continuePayment.cashierPath
      ).trim() || DEFAULT_CHECKOUT_CONFIG.continuePayment.cashierPath,
      paymentPath: String(
        templateCheckoutConfig.continuePayment?.paymentPath
          || DEFAULT_CHECKOUT_CONFIG.continuePayment.paymentPath
      ).trim() || DEFAULT_CHECKOUT_CONFIG.continuePayment.paymentPath,
    },
    addressLibrary: {
      ...DEFAULT_CHECKOUT_CONFIG.addressLibrary,
      ...(templateCheckoutConfig.addressLibrary || {}),
      source: String(
        templateCheckoutConfig.addressLibrary?.source
          || DEFAULT_CHECKOUT_CONFIG.addressLibrary.source
      ).trim().toLowerCase() || DEFAULT_CHECKOUT_CONFIG.addressLibrary.source,
      dataset: String(
        templateCheckoutConfig.addressLibrary?.dataset
          || DEFAULT_CHECKOUT_CONFIG.addressLibrary.dataset
      ).trim(),
    },
    forms: {
      ...DEFAULT_CHECKOUT_FORM_CONFIG,
      ...(templateCheckoutConfig.forms || {}),
      shippingAddress: {
        ...DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress,
        ...(templateCheckoutConfig.forms?.shippingAddress || {}),
        fields: {
          ...DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields,
          ...(templateCheckoutConfig.forms?.shippingAddress?.fields || {}),
          postalCode: {
            ...DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode,
            ...(templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode || {}),
            enabled: typeof templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.enabled === "boolean"
              ? templateCheckoutConfig.forms.shippingAddress.fields.postalCode.enabled
              : DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.enabled,
            required: typeof templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.required === "boolean"
              ? templateCheckoutConfig.forms.shippingAddress.fields.postalCode.required
              : DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.required,
            normalize: String(
              templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.normalize
              || DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.normalize
            ).trim().toLowerCase(),
            maxLength: Number(
              templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.maxLength
              || DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.maxLength
            ),
            pattern: String(
              templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.pattern
              || DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.pattern
            ).trim(),
            requiredMessageKey: String(
              templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.requiredMessageKey
              || DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.requiredMessageKey
            ).trim(),
            invalidMessageKey: String(
              templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.invalidMessageKey
              || DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.invalidMessageKey
            ).trim(),
            payment: {
              ...DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.payment,
              ...(templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.payment || {}),
            },
            addressModal: {
              ...DEFAULT_CHECKOUT_FORM_CONFIG.shippingAddress.fields.postalCode.addressModal,
              ...(templateCheckoutConfig.forms?.shippingAddress?.fields?.postalCode?.addressModal || {}),
            },
          },
        },
      },
    },
  };

  const presetKey = mergedConfig.phone?.preset || DEFAULT_CHECKOUT_CONFIG.phone.preset;
  const preset = PHONE_PRESETS[presetKey] || PHONE_PRESETS.tw;

  return {
    ...mergedConfig,
    phone: {
      ...preset,
      ...mergedConfig.phone,
      payment: {
        ...preset.payment,
        ...(mergedConfig.phone?.payment || {}),
      },
      addressModal: {
        ...preset.addressModal,
        ...(mergedConfig.phone?.addressModal || {}),
      },
      preset: presetKey,
    },
  };
}

export function normalizeTemplatePhone(value, phoneConfig = {}) {
  const maxLength = Number(phoneConfig.maxLength || PHONE_PRESETS.tw.maxLength);
  return String(value || "")
    .replace(/[^\d]/g, "")
    .slice(0, maxLength);
}

export function validateTemplatePhone(value, phoneConfig = {}) {
  const normalizedValue = normalizeTemplatePhone(value, phoneConfig);
  const pattern = phoneConfig.pattern || PHONE_PRESETS.tw.pattern;

  return {
    normalizedValue,
    isValid: pattern.test(normalizedValue),
  };
}

export function resolveTemplateFormConfig(checkoutConfig = {}, formKey = "") {
  return checkoutConfig?.forms?.[formKey] || {};
}

export function resolveTemplateFormFieldConfig(checkoutConfig = {}, formKey = "", fieldKey = "") {
  return resolveTemplateFormConfig(checkoutConfig, formKey)?.fields?.[fieldKey] || {};
}

export function normalizeTemplateFormFieldValue(value, fieldConfig = {}) {
  const normalizeMode = String(fieldConfig.normalize || "text").trim().toLowerCase();
  const maxLength = Number(fieldConfig.maxLength || 0);
  let normalizedValue = `${value ?? ""}`;

  if (normalizeMode === "digits") {
    normalizedValue = normalizedValue.replace(/\D/g, "");
  } else {
    normalizedValue = normalizedValue.trimStart();
  }

  if (Number.isFinite(maxLength) && maxLength > 0) {
    normalizedValue = normalizedValue.slice(0, maxLength);
  }

  return normalizedValue;
}

export function validateTemplateFormFieldValue(value, fieldConfig = {}) {
  const normalizedValue = normalizeTemplateFormFieldValue(value, fieldConfig);
  const required = Boolean(fieldConfig.required);

  if (required && !normalizedValue.trim()) {
    return {
      normalizedValue,
      isValid: false,
      errorKey: fieldConfig.requiredMessageKey || "",
    };
  }

  const patternValue = String(fieldConfig.pattern || "").trim();
  if (patternValue) {
    try {
      const pattern = new RegExp(patternValue);
      if (normalizedValue && !pattern.test(normalizedValue)) {
        return {
          normalizedValue,
          isValid: false,
          errorKey: fieldConfig.invalidMessageKey || fieldConfig.requiredMessageKey || "",
        };
      }
    } catch (error) {
      return {
        normalizedValue,
        isValid: false,
        errorKey: fieldConfig.invalidMessageKey || fieldConfig.requiredMessageKey || "",
      };
    }
  }

  return {
    normalizedValue,
    isValid: true,
    errorKey: "",
  };
}

export async function resolveTemplateAddressTree(checkoutConfig = {}, remoteLoader) {
  const resolvedLibrary = await resolveTemplateAddressLibrary(checkoutConfig, remoteLoader);
  return resolvedLibrary.tree;
}

export async function resolveTemplateAddressLibrary(checkoutConfig = {}, remoteLoader) {
  const addressLibraryConfig = checkoutConfig?.addressLibrary || {};
  const source = String(addressLibraryConfig.source || "remote").trim().toLowerCase();
  const dataset = String(addressLibraryConfig.dataset || "").trim();

  if (source === "local" && dataset) {
    const localAddressTree = getLocalAddressLibrary(dataset);
    if (localAddressTree.length) {
      return {
        source: "local",
        dataset,
        meta: getLocalAddressLibraryMeta(dataset),
        tree: localAddressTree,
      };
    }
  }

  if (typeof remoteLoader !== "function") {
    return {
      source: "remote",
      dataset: "",
      meta: null,
      tree: [],
    };
  }

  const response = await remoteLoader();
  return {
    source: "remote",
    dataset: "",
    meta: null,
    tree: Array.isArray(response?.data?.[0]?.childrenAddress)
      ? response.data[0].childrenAddress
      : [],
  };
}

export default {
  resolveTemplateCheckoutConfig,
  resolveTemplateFormConfig,
  resolveTemplateFormFieldConfig,
  normalizeTemplatePhone,
  normalizeTemplateFormFieldValue,
  validateTemplateFormFieldValue,
  validateTemplatePhone,
  resolveTemplateAddressTree,
  resolveTemplateAddressLibrary,
};
